import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const PendingOperationsContext = createContext();

export const usePendingOperations = () => {
  const context = useContext(PendingOperationsContext);
  if (!context) {
    throw new Error('usePendingOperations must be used within PendingOperationsProvider');
  }
  return context;
};

export const PendingOperationsProvider = ({ children }) => {
  const [pendingCount, setPendingCount] = useState(0);
  const [isNavigationBlocked, setIsNavigationBlocked] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const pendingOperations = useRef(new Set());
  const operationTimeouts = useRef(new Map());

  // Start a pending operation (returns an ID to track it)
  const startOperation = useCallback((operationName = 'unknown') => {
    const operationId = `${operationName}-${Date.now()}-${Math.random()}`;
    pendingOperations.current.add(operationId);
    setPendingCount(pendingOperations.current.size);
    
    // Safety timeout - auto-complete after 30 seconds to prevent stuck operations
    const timeoutId = setTimeout(() => {
      if (pendingOperations.current.has(operationId)) {
        pendingOperations.current.delete(operationId);
        operationTimeouts.current.delete(operationId);
        setPendingCount(pendingOperations.current.size);
      }
    }, 35000);
    operationTimeouts.current.set(operationId, timeoutId);
    
    return operationId;
  }, []);

  // Complete a pending operation
  const completeOperation = useCallback((operationId) => {
    // Clear the safety timeout
    const timeoutId = operationTimeouts.current.get(operationId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      operationTimeouts.current.delete(operationId);
    }
    
    pendingOperations.current.delete(operationId);
    setPendingCount(pendingOperations.current.size);
    
    // If no more pending operations and there's a pending navigation, execute it
    if (pendingOperations.current.size === 0 && pendingNavigation) {
      const navFn = pendingNavigation;
      setPendingNavigation(null);
      setIsNavigationBlocked(false);
      // Execute on next tick to ensure state updates complete
      setTimeout(() => navFn(), 0);
    }
  }, [pendingNavigation]);

  // Wrap an async operation to track it automatically
  const trackOperation = useCallback(async (operationName, asyncFn) => {
    const operationId = startOperation(operationName);
    try {
      const result = await asyncFn();
      return result;
    } finally {
      completeOperation(operationId);
    }
  }, [startOperation, completeOperation]);

  // Check if navigation should be blocked, and queue it if so
  const requestNavigation = useCallback((navigationFn) => {
    if (pendingOperations.current.size > 0) {
      setIsNavigationBlocked(true);
      setPendingNavigation(() => navigationFn);
      return false; // Navigation was blocked
    }
    // No pending operations, navigate immediately
    navigationFn();
    return true; // Navigation executed
  }, []);

  // Force clear all operations (emergency use / debugging)
  const clearAllOperations = useCallback(() => {
    // Clear all timeouts
    for (const timeoutId of operationTimeouts.current.values()) {
      clearTimeout(timeoutId);
    }
    operationTimeouts.current.clear();
    pendingOperations.current.clear();
    setPendingCount(0);
    setIsNavigationBlocked(false);
    setPendingNavigation(null);
  }, []);

  // Check if there are pending operations
  const hasPendingOperations = pendingCount > 0;

  return (
    <PendingOperationsContext.Provider value={{
      startOperation,
      completeOperation,
      trackOperation,
      requestNavigation,
      clearAllOperations,
      hasPendingOperations,
      pendingCount,
      isNavigationBlocked
    }}>
      {children}
      
      {/* Global loading overlay when navigation is blocked */}
      {isNavigationBlocked && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          color: 'white',
          fontSize: '18px'
        }}>
          <div className="loading-spinner" style={{
            width: '50px',
            height: '50px',
            border: '4px solid rgba(255, 255, 255, 0.3)',
            borderTop: '4px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }} />
          <div>Saving changes...</div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}
    </PendingOperationsContext.Provider>
  );
};

export default PendingOperationsContext;
